/** @format */

import {
	Box,
	Button,
	Card,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Grid,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	IconButton,
	Tooltip,
	CircularProgress,
	Avatar,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import { CheckCircle } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { XCircle } from "@phosphor-icons/react/dist/ssr/XCircle";
import { ArrowsClockwise } from "@phosphor-icons/react/dist/ssr/ArrowsClockwise";
import InfoIcon from "@mui/icons-material/Info";
import GroupsIcon from "@mui/icons-material/Groups";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import {
	fetchAllAddRequestsAsAdmin,
	approveAddRequest,
	rejectAddRequest,
} from "../../../redux/actions/circleAction";
import {
	selectAddRequestsApplications,
	selectIsLoadingAddRequests,
	selectIsApprovingAddRequest,
	selectIsRejectingAddRequest,
} from "../../../redux/selectors/circleSelector";
import {
	LoadingSpinner,
	SuccessNotification,
} from "../../allComponents/AllComponents";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import Main from "../../../utility/common/Main";
import { AdminAddRequestItem } from "../../../utility/types/circleType";

const AddRequestsApplications = () => {
	const dispatch = useDispatch<AppDispatch>();
	const addRequestsData = useSelector(selectAddRequestsApplications);
	const isLoading = useSelector(selectIsLoadingAddRequests);
	const isApproving = useSelector(selectIsApprovingAddRequest);
	const isRejecting = useSelector(selectIsRejectingAddRequest);
	const [openNav, setOpenNav] = useState(false);

	// Track which specific add request is being processed
	const [processingRequestId, setProcessingRequestId] = useState<string | null>(
		null,
	);
	// Track if modal action is processing
	const [isProcessing, setIsProcessing] = useState(false);

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [confirmDialog, setConfirmDialog] = useState<{
		open: boolean;
		type: "approve" | "reject" | null;
		addRequestId: string | null;
		memberName: string;
		circleName: string;
	}>({
		open: false,
		type: null,
		addRequestId: null,
		memberName: "",
		circleName: "",
	});
	const [detailsModal, setDetailsModal] = useState<{
		open: boolean;
		addRequest: AdminAddRequestItem | null;
	}>({
		open: false,
		addRequest: null,
	});
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success" as "success" | "error" | "info" | "warning",
	});

	useEffect(() => {
		dispatch(fetchAllAddRequestsAsAdmin());
	}, [dispatch]);

	useEffect(() => {
		// Reset processing state when approving/rejecting completes
		if (!isApproving && !isRejecting) {
			setProcessingRequestId(null);
			setIsProcessing(false);
		}
	}, [isApproving, isRejecting]);

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(event.target.value);
		setPage(0);
	};

	const handleStatusFilter = (status: string) => {
		setStatusFilter(status);
		setPage(0);
	};

	const handleRefresh = () => {
		dispatch(fetchAllAddRequestsAsAdmin());
		setNotification({
			open: true,
			message: "Add requests refreshed",
			severity: "success",
		});
	};

	const handleOpenConfirmDialog = (
		type: "approve" | "reject",
		addRequestId: string,
		memberName: string,
		circleName: string,
	) => {
		setConfirmDialog({
			open: true,
			type,
			addRequestId,
			memberName,
			circleName,
		});
	};

	const handleCloseConfirmDialog = () => {
		setConfirmDialog({
			open: false,
			type: null,
			addRequestId: null,
			memberName: "",
			circleName: "",
		});
	};

	const handleConfirmAction = async () => {
		if (!confirmDialog.addRequestId || !confirmDialog.type) return;

		setProcessingRequestId(confirmDialog.addRequestId);
		setIsProcessing(true);

		try {
			if (confirmDialog.type === "approve") {
				await dispatch(approveAddRequest(confirmDialog.addRequestId)).unwrap();
				setNotification({
					open: true,
					message: `Successfully approved add request for ${confirmDialog.memberName}`,
					severity: "success",
				});
			} else {
				await dispatch(rejectAddRequest(confirmDialog.addRequestId)).unwrap();
				setNotification({
					open: true,
					message: `Successfully rejected add request for ${confirmDialog.memberName}`,
					severity: "success",
				});
			}
			handleCloseConfirmDialog();
		} catch (error: any) {
			// Extract user-friendly error message
			let errorMessage = "Failed to process add request. Please try again.";

			if (error) {
				// Handle different error formats
				if (typeof error === "string") {
					// Clean up technical error messages
					if (
						error.includes("validation failed") ||
						error.includes("Cast to embedded")
					) {
						errorMessage = `Unable to ${confirmDialog.type} the request. Please refresh and try again.`;
					} else if (error.includes("not found")) {
						errorMessage =
							"This request no longer exists. Please refresh the page.";
					} else if (error.includes("already")) {
						errorMessage = error; // "Already approved/rejected" messages are user-friendly
					} else {
						errorMessage = error;
					}
				} else if (error.message) {
					errorMessage = error.message;
				}
			}

			setNotification({
				open: true,
				message: errorMessage,
				severity: "error",
			});
		} finally {
			setProcessingRequestId(null);
			setIsProcessing(false);
		}
	};

	const handleOpenDetailsModal = (addRequest: AdminAddRequestItem) => {
		setDetailsModal({
			open: true,
			addRequest,
		});
	};

	const handleCloseDetailsModal = () => {
		setDetailsModal({
			open: false,
			addRequest: null,
		});
	};

	const handleCloseNotification = () => {
		setNotification({ ...notification, open: false });
	};

	// Filter add requests
	const filteredRequests = addRequestsData?.data?.filter((request) => {
		const matchesSearch =
			request.member.firstName
				.toLowerCase()
				.includes(searchQuery.toLowerCase()) ||
			request.member.lastName
				.toLowerCase()
				.includes(searchQuery.toLowerCase()) ||
			request.member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
			request.union.circleName
				.toLowerCase()
				.includes(searchQuery.toLowerCase()) ||
			request.union.leader.firstName
				.toLowerCase()
				.includes(searchQuery.toLowerCase()) ||
			request.union.leader.lastName
				.toLowerCase()
				.includes(searchQuery.toLowerCase());

		const matchesStatus =
			statusFilter === "all" || request.status === statusFilter;

		return matchesSearch && matchesStatus;
	});

	const paginatedRequests = filteredRequests?.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage,
	);

	const getStatusColor = (
		status: string,
	): "default" | "success" | "error" | "warning" => {
		switch (status) {
			case "Approved":
				return "success";
			case "Rejected":
				return "error";
			case "Pending":
				return "warning";
			default:
				return "default";
		}
	};

	return (
		<React.Fragment>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />

			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />
				<Main>
					{isLoading ?
						<LoadingSpinner loading={isLoading} size={45} />
					:	<>
							<Stack spacing={3}>
								<Stack direction="row" spacing={3}>
									<Stack spacing={1} sx={{ flex: "1 1 auto" }}>
										<Typography variant="h4">
											Add Request Applications
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Manage circle leader invitations sent to platform members
										</Typography>
									</Stack>
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<Tooltip title="Refresh">
											<IconButton onClick={handleRefresh} disabled={isLoading}>
												<ArrowsClockwise />
											</IconButton>
										</Tooltip>
									</Box>
								</Stack>

								{/* Stats Cards */}
								<Stack direction="row" spacing={2}>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="primary">
											{addRequestsData?.count || 0}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Total Requests
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="warning.main">
											{
												filteredRequests?.filter((r) => r.status === "Pending")
													.length
											}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Pending
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="success.main">
											{
												filteredRequests?.filter((r) => r.status === "Approved")
													.length
											}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Approved
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="error.main">
											{
												filteredRequests?.filter((r) => r.status === "Rejected")
													.length
											}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Rejected
										</Typography>
									</Card>
								</Stack>

								{/* Filters and Search */}
								<Card>
									<Stack
										direction="row"
										spacing={2}
										sx={{ p: 2 }}
										alignItems="center">
										<OutlinedInput
											fullWidth
											placeholder="Search by member name, email, circle, or leader"
											startAdornment={
												<InputAdornment position="start">
													<MagnifyingGlassIcon fontSize="var(--icon-fontSize-md)" />
												</InputAdornment>
											}
											value={searchQuery}
											onChange={handleSearch}
										/>
										<Stack direction="row" spacing={1}>
											<Button
												variant={
													statusFilter === "all" ? "contained" : "outlined"
												}
												onClick={() => handleStatusFilter("all")}
												size="small">
												All
											</Button>
											<Button
												variant={
													statusFilter === "Pending" ? "contained" : "outlined"
												}
												onClick={() => handleStatusFilter("Pending")}
												size="small"
												color="warning">
												Pending
											</Button>
											<Button
												variant={
													statusFilter === "Approved" ? "contained" : "outlined"
												}
												onClick={() => handleStatusFilter("Approved")}
												size="small"
												color="success">
												Approved
											</Button>
											<Button
												variant={
													statusFilter === "Rejected" ? "contained" : "outlined"
												}
												onClick={() => handleStatusFilter("Rejected")}
												size="small"
												color="error">
												Rejected
											</Button>
										</Stack>
									</Stack>
								</Card>

								{/* Table */}
								<Card>
									<Box sx={{ overflowX: "auto" }}>
										<Table sx={{ minWidth: "800px" }}>
											<TableHead>
												<TableRow>
													<TableCell>Member</TableCell>
													<TableCell>Circle</TableCell>
													<TableCell>Circle Leader</TableCell>
													<TableCell>Status</TableCell>
													<TableCell>Requested Date</TableCell>
													<TableCell align="center">More Info</TableCell>
													<TableCell align="center">Actions</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{paginatedRequests && paginatedRequests.length > 0 ?
													paginatedRequests.map((request) => (
														<TableRow hover key={request._id}>
															<TableCell>
																<Stack
																	direction="row"
																	spacing={2}
																	alignItems="center">
																	<Avatar
																		src={request.member.profileImage}
																		alt={`${request.member.firstName} ${request.member.lastName}`}
																	/>
																	<Stack>
																		<Typography variant="subtitle2">
																			{request.member.firstName}{" "}
																			{request.member.lastName}
																		</Typography>
																		<Typography
																			variant="body2"
																			color="text.secondary">
																			{request.member.email}
																		</Typography>
																	</Stack>
																</Stack>
															</TableCell>
															<TableCell>
																<Stack
																	direction="row"
																	spacing={1}
																	alignItems="center">
																	{request.union.circleImage && (
																		<Avatar
																			src={request.union.circleImage}
																			alt={request.union.circleName}
																			sx={{ width: 32, height: 32 }}
																		/>
																	)}
																	<Stack>
																		<Typography variant="subtitle2">
																			{request.union.circleName}
																		</Typography>
																		{request.union.totalMembers !==
																			undefined && (
																			<Typography
																				variant="caption"
																				color="text.secondary">
																				{request.union.totalMembers} members
																			</Typography>
																		)}
																	</Stack>
																</Stack>
															</TableCell>
															<TableCell>
																<Typography variant="body2">
																	{request.union.leader.firstName}{" "}
																	{request.union.leader.lastName}
																</Typography>
																<Typography
																	variant="caption"
																	color="text.secondary">
																	{request.union.leader.email}
																</Typography>
															</TableCell>
															<TableCell>
																<Chip
																	label={request.status}
																	color={getStatusColor(request.status)}
																	size="small"
																/>
															</TableCell>
															<TableCell>
																{dayjs(request.createdAt).format("MMM D, YYYY")}
																<Typography
																	variant="caption"
																	display="block"
																	color="text.secondary">
																	{dayjs(request.createdAt).format("h:mm A")}
																</Typography>
															</TableCell>
															<TableCell align="center">
																<Tooltip title="More Info">
																	<IconButton
																		onClick={() =>
																			handleOpenDetailsModal(request)
																		}>
																		<InfoIcon />
																	</IconButton>
																</Tooltip>
															</TableCell>
															<TableCell align="center">
																{request.status === "Pending" && (
																	<Stack
																		direction="row"
																		spacing={1}
																		justifyContent="center">
																		<Tooltip title="Approve">
																			<IconButton
																				color="success"
																				onClick={() =>
																					handleOpenConfirmDialog(
																						"approve",
																						request._id,
																						`${request.member.firstName} ${request.member.lastName}`,
																						request.union.circleName,
																					)
																				}
																				disabled={
																					isApproving ||
																					isRejecting ||
																					processingRequestId === request._id
																				}>
																				{(
																					processingRequestId === request._id &&
																					isApproving
																				) ?
																					<CircularProgress size={20} />
																				:	<CheckCircle />}
																			</IconButton>
																		</Tooltip>
																		<Tooltip title="Reject">
																			<IconButton
																				color="error"
																				onClick={() =>
																					handleOpenConfirmDialog(
																						"reject",
																						request._id,
																						`${request.member.firstName} ${request.member.lastName}`,
																						request.union.circleName,
																					)
																				}
																				disabled={
																					isApproving ||
																					isRejecting ||
																					processingRequestId === request._id
																				}>
																				{(
																					processingRequestId === request._id &&
																					isRejecting
																				) ?
																					<CircularProgress size={20} />
																				:	<XCircle />}
																			</IconButton>
																		</Tooltip>
																	</Stack>
																)}
															</TableCell>
														</TableRow>
													))
												:	<TableRow>
														<TableCell colSpan={7} align="center">
															<Typography
																variant="body2"
																color="text.secondary">
																No add requests found
															</Typography>
														</TableCell>
													</TableRow>
												}
											</TableBody>
										</Table>
									</Box>
									<Divider />
									<TablePagination
										component="div"
										count={filteredRequests?.length || 0}
										onPageChange={handleChangePage}
										onRowsPerPageChange={handleChangeRowsPerPage}
										page={page}
										rowsPerPage={rowsPerPage}
										rowsPerPageOptions={[5, 10, 25]}
									/>
								</Card>

								{/* Confirm Dialog */}
								<Dialog
									open={confirmDialog.open}
									onClose={handleCloseConfirmDialog}>
									<DialogTitle>
										{confirmDialog.type === "approve" ?
											"Approve Add Request"
										:	"Reject Add Request"}
									</DialogTitle>
									<DialogContent>
										<DialogContentText>
											Are you sure you want to {confirmDialog.type}{" "}
											<strong>{confirmDialog.memberName}</strong>'s invitation
											to join <strong>{confirmDialog.circleName}</strong>?
										</DialogContentText>
									</DialogContent>
									<DialogActions>
										<Button
											onClick={handleCloseConfirmDialog}
											disabled={isProcessing}>
											Cancel
										</Button>
										<Button
											onClick={handleConfirmAction}
											color={
												confirmDialog.type === "approve" ? "success" : "error"
											}
											variant="contained"
											disabled={isProcessing}>
											{isProcessing ?
												<CircularProgress size={20} />
											: confirmDialog.type === "approve" ?
												"Approve"
											:	"Reject"}
										</Button>
									</DialogActions>
								</Dialog>

								{/* Details Modal */}
								<Dialog
									open={detailsModal.open}
									onClose={handleCloseDetailsModal}
									maxWidth="md"
									fullWidth
									PaperProps={{
										sx: {
											borderRadius: 3,
											overflow: "hidden",
										},
									}}>
									<DialogTitle
										sx={{
											background:
												"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
											color: "white",
											py: 2.5,
										}}>
										<Typography variant="h5" fontWeight={600}>
											Add Request Details
										</Typography>
									</DialogTitle>
									<DialogContent sx={{ p: 0 }}>
										{detailsModal.addRequest && (
											<Box>
												{/* Member Profile Header */}
												<Box
													sx={{
														background:
															"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
														pt: 0,
														pb: 8,
														px: 3,
													}}>
													<Stack
														direction="row"
														spacing={3}
														alignItems="center">
														<Avatar
															src={detailsModal.addRequest.member.profileImage}
															sx={{
																width: 80,
																height: 80,
																border: "4px solid white",
																boxShadow: 3,
															}}>
															{`${detailsModal.addRequest.member.firstName?.[0]}${detailsModal.addRequest.member.lastName?.[0]}`}
														</Avatar>
														<Box>
															<Typography
																variant="h5"
																fontWeight={600}
																color="white">
																{detailsModal.addRequest.member.firstName}{" "}
																{detailsModal.addRequest.member.lastName}
															</Typography>
															<Typography
																variant="body2"
																sx={{ color: "rgba(255,255,255,0.85)" }}>
																{detailsModal.addRequest.member.email}
															</Typography>
														</Box>
													</Stack>
												</Box>

												{/* Status Cards - Overlapping Design */}
												<Box sx={{ px: 3, mt: -5 }}>
													<Grid container spacing={2}>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		(
																			detailsModal.addRequest.status?.toLowerCase() ===
																			"accepted"
																		) ?
																			"linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
																		: (
																			detailsModal.addRequest.status?.toLowerCase() ===
																			"rejected"
																		) ?
																			"linear-gradient(135deg, #eb3349 0%, #f45c43 100%)"
																		:	"linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
																	color: "white",
																}}>
																<HourglassEmptyIcon
																	sx={{ fontSize: 32, mb: 1 }}
																/>
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Request Status
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.addRequest.status}
																</Typography>
															</Card>
														</Grid>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
																	color: "white",
																}}>
																<PersonAddIcon sx={{ fontSize: 32, mb: 1 }} />
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Invitation Type
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	Leader Invite
																</Typography>
															</Card>
														</Grid>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		"linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
																	color: "white",
																}}>
																<GroupsIcon sx={{ fontSize: 32, mb: 1 }} />
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Circle Members
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.addRequest.union.totalMembers ||
																		0}
																</Typography>
															</Card>
														</Grid>
													</Grid>
												</Box>

												{/* Circle Information Section */}
												<Box sx={{ px: 3, py: 3 }}>
													<Typography
														variant="subtitle1"
														fontWeight={600}
														sx={{ mb: 2, color: "text.secondary" }}>
														Circle Information
													</Typography>
													<Card
														variant="outlined"
														sx={{
															borderRadius: 2,
															transition: "all 0.2s ease-in-out",
															"&:hover": {
																boxShadow: 2,
																borderColor: "primary.main",
															},
														}}>
														<Stack
															direction="row"
															spacing={2}
															alignItems="center"
															sx={{ p: 2 }}>
															<Avatar
																src={detailsModal.addRequest.union.circleImage}
																alt={detailsModal.addRequest.union.circleName}
																sx={{
																	width: 56,
																	height: 56,
																	borderRadius: 2,
																}}
																variant="rounded"
															/>
															<Box sx={{ flex: 1 }}>
																<Typography
																	variant="subtitle1"
																	fontWeight={600}>
																	{detailsModal.addRequest.union.circleName}
																</Typography>
																<Stack
																	direction="row"
																	spacing={2}
																	sx={{ mt: 0.5 }}>
																	<Typography
																		variant="body2"
																		color="text.secondary">
																		Leader:{" "}
																		<strong>
																			{
																				detailsModal.addRequest.union.leader
																					.firstName
																			}{" "}
																			{
																				detailsModal.addRequest.union.leader
																					.lastName
																			}
																		</strong>
																	</Typography>
																</Stack>
															</Box>
														</Stack>
													</Card>
												</Box>

												{/* Circle Leader Section */}
												<Box sx={{ px: 3, pb: 3 }}>
													<Typography
														variant="subtitle1"
														fontWeight={600}
														sx={{ mb: 2, color: "text.secondary" }}>
														Circle Leader Details
													</Typography>
													<Card
														variant="outlined"
														sx={{ borderRadius: 2, overflow: "hidden" }}>
														<Stack divider={<Divider />}>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Leader Name
																</Typography>
																<Typography variant="body1" fontWeight={500}>
																	{
																		detailsModal.addRequest.union.leader
																			.firstName
																	}{" "}
																	{
																		detailsModal.addRequest.union.leader
																			.lastName
																	}
																</Typography>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Leader Email
																</Typography>
																<Typography variant="body1" fontWeight={500}>
																	{detailsModal.addRequest.union.leader.email}
																</Typography>
															</Stack>
														</Stack>
													</Card>
												</Box>

												{/* Request Timeline Section */}
												<Box sx={{ px: 3, pb: 3 }}>
													<Typography
														variant="subtitle1"
														fontWeight={600}
														sx={{ mb: 2, color: "text.secondary" }}>
														Request Timeline
													</Typography>
													<Card
														variant="outlined"
														sx={{ borderRadius: 2, overflow: "hidden" }}>
														<Stack divider={<Divider />}>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Stack
																	direction="row"
																	spacing={1}
																	alignItems="center">
																	<CalendarTodayIcon
																		fontSize="small"
																		color="action"
																	/>
																	<Typography
																		variant="body2"
																		color="text.secondary">
																		Sent
																	</Typography>
																</Stack>
																<Typography variant="body1" fontWeight={500}>
																	{dayjs(
																		detailsModal.addRequest.createdAt,
																	).format("MMM D, YYYY [at] h:mm A")}
																</Typography>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Last Updated
																</Typography>
																<Typography variant="body1" fontWeight={500}>
																	{dayjs(
																		detailsModal.addRequest.updatedAt,
																	).format("MMM D, YYYY [at] h:mm A")}
																</Typography>
															</Stack>
														</Stack>
													</Card>
												</Box>
											</Box>
										)}
									</DialogContent>
									<DialogActions sx={{ px: 3, py: 2 }}>
										<Button
											onClick={handleCloseDetailsModal}
											variant="outlined"
											sx={{ borderRadius: 2, px: 3 }}>
											Close
										</Button>
									</DialogActions>
								</Dialog>

								{/* Notification */}
								<SuccessNotification
									open={notification.open}
									onClose={handleCloseNotification}
									severity={notification.severity}
									message={notification.message}
								/>
							</Stack>
						</>
					}
				</Main>
			</Box>
		</React.Fragment>
	);
};

export default AddRequestsApplications;
